import {
  ApplicationContext,
  EnvironmentContext,
  PlatformEnvironment,
} from '@wix/editor-platform-contexts';
import {
  ManifestServicesHost,
  IVirtualManifestBuilderProps,
  IVirtualManifest,
} from '@wix/editor-platform-manifest-services';
// TODO: replace with ambassador-tokencreator-v1-token/types
import {
  AccessToken,
  OverriddenSelectBindingOptions,
  WixSDKTypes,
} from '@wix/public-editor-platform-interfaces';
import {
  IPrivateAPI,
  PrivateAPINamespace,
} from '@wix/editor-platform-private-api';
import { ReadOnlyExperiments } from '@wix/wix-experiments';
import { PlatformAppEventEmitter } from '@wix/public-editor-platform-events';
import { OverriddenColorPickerOptions } from '@wix/editor-platform-sdk-types';

import {
  createEditorPlatformWorkerError,
  EditorPlatformWorkerErrorCode,
} from './errors';
import { WorkspaceHost, Workspace } from '@wix/editor';

export class PlatformSDKHost {
  #registry = new FinalizationRegistry((..._props) => {
    /**
     * we need to find a good way to track host is not used anymore
     * as soon as it is garbage-collected –
     * we need to clear local states in a platform, related to the destroyed host
     */
    // console.log(`hosts are collected`, { props });
  });

  constructor(
    private readonly privateAPI: IPrivateAPI,
    private readonly manifestServicesHost: ManifestServicesHost,
  ) {}

  getSDKAuth(
    props: {
      appDefinitionId?: string;
    } = {},
  ) {
    let instance: AccessToken;
    const appDefinitionId = props.appDefinitionId;

    return {
      getAuthHeaders: async () => {
        if (!appDefinitionId) {
          throw createEditorPlatformWorkerError(
            EditorPlatformWorkerErrorCode.AuthEmptyAppDefinitionId,
          );
        }

        if (!instance || hasEnoughTimeUntilExpiry(instance.expires)) {
          instance = await this.privateAPI.auth.getAccessToken(
            appDefinitionId,
            await this.privateAPI.info.getMetaSiteId(),
          );
        }

        if (!instance?.token) {
          console.error(
            createEditorPlatformWorkerError(
              EditorPlatformWorkerErrorCode.AuthEmptyAppAuthInstance,
            ).withAppDefinitionId(appDefinitionId),
          );

          return {
            headers: {
              Authorization: '',
            },
          };
        }

        return {
          headers: {
            Authorization: instance.token,
          },
        };
      },
    };
  }

  #bindPrivateApiToApp(appDefinitionId?: string): IPrivateAPI {
    const bound = {} as IPrivateAPI;
    for (const [key, value] of Object.entries(this.privateAPI)) {
      (bound as Record<string, unknown>)[key] =
        value instanceof PrivateAPINamespace
          ? value.withAppContext(appDefinitionId)
          : value;
    }
    return bound;
  }

  #getSDKHost({
    manifestContextId,
    appDefinitionId,
    metadata,
  }: {
    metadata?: IVirtualManifest['metadata'];
    manifestContextId?: string;
    appDefinitionId?: string;
  }): WixSDKTypes.Host & {
    workspace: Workspace;
    applicationContext: ApplicationContext;
    environmentContext: EnvironmentContext;
    metadata: IVirtualManifest['metadata'] | undefined;
  } {
    const privateApi = this.#bindPrivateApiToApp(appDefinitionId);
    const environmentContext = new EnvironmentContext({
      environment: PlatformEnvironment.ComponentPanel,
      privateApi,
      events: new PlatformAppEventEmitter(),
    });

    const applicationContext = new ApplicationContext(
      {
        manifestContextId,
        appDefinitionId,
      },
      environmentContext,
    );

    const workspaceHost = new WorkspaceHost(
      Promise.resolve(applicationContext),
    );

    const experiments = new ReadOnlyExperiments({
      experiments: privateApi.experiments.all(),
    });
    const language = privateApi.info.getLanguageCode();

    const host = {
      channel: {
        observeState: async () => {
          return { disconnect() {} };
        },
      },
      getExperiments: () => experiments,
      essentials: {
        language,
      },
      metadata,
      environmentContext,
      applicationContext,
      workspace: workspaceHost.getAPI(),
    };

    if (manifestContextId) {
      this.#registry.register(host, manifestContextId);
    }

    return host;
  }

  getSDKHost(
    props: {
      appDefinitionId?: string;
      contextType?: string;
      overriddenColorPickerOptions?: OverriddenColorPickerOptions;
      overriddenSelectBindingOptions?: OverriddenSelectBindingOptions;
    } & IVirtualManifestBuilderProps,
  ) {
    const {
      appDefinitionId,
      contextType,
      overriddenColorPickerOptions,
      overriddenSelectBindingOptions,
      ...manifestBuilderProps
    } = props;

    let manifestContextId: string | null = null;

    try {
      manifestContextId =
        contextType && !manifestBuilderProps.contextBuilder
          ? this.manifestServicesHost.buildContextualProviderManifest(
              contextType,
              manifestBuilderProps,
              {
                overriddenColorPickerOptions,
                overriddenSelectBindingOptions,
              },
            )
          : this.manifestServicesHost.buildContextualManifest(
              manifestBuilderProps,
              {
                overriddenColorPickerOptions,
                overriddenSelectBindingOptions,
              },
            );
    } catch (e) {
      console.warn(e);
    }

    return this.#getSDKHost({
      appDefinitionId,
      manifestContextId: manifestContextId ? manifestContextId : undefined,
    });
  }

  getSDKHosts() {
    // TODO: remove all types
    return [];
  }

  getSDKHostContext(
    props: {
      appDefinitionId?: string;
      contextType?: string;
    } & IVirtualManifestBuilderProps,
  ) {
    return {
      getHost: () => {
        return this.getSDKHost(props);
      },
      getHosts: () => {
        return this.getSDKHosts();
      },
      getAuth: () => {
        return this.getSDKAuth({
          appDefinitionId: props.appDefinitionId,
        });
      },
    };
  }
}

const ACCESS_TOKEN_EXPIRY_GRACE_PERIOD = 30 * 60 * 1000; // half an hour

function hasEnoughTimeUntilExpiry(
  expires: Date | undefined,
  expiryBuffer = ACCESS_TOKEN_EXPIRY_GRACE_PERIOD,
) {
  const now = Date.now();
  const expiryTime = expires ? new Date(expires).getTime() : null;
  return expiryTime && expiryTime - now > expiryBuffer;
}
